import { useTranslation } from 'react-i18next';
import { Card, Tag } from '@/shared/ui/primitives';
import { useTranslationCoverage } from '../hooks/useTranslations';

export default function CoveragePanel({ open }: { open: boolean }) {
  const { t } = useTranslation();
  const q = useTranslationCoverage(open);
  if (!open) return null;

  if (q.isLoading) {
    return <div style={{ padding: 16, color: '#666' }}>{t('translations.health.loading')}</div>;
  }
  if (q.isError || !q.data) {
    return (
      <div style={{ padding: 16, color: '#d4380d' }}>
        {t('translations.health.failed')}
      </div>
    );
  }

  const data = q.data;

  return (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <strong>{t('translations.health.title')}</strong>
        <span style={{ color: '#666' }}>{t('translations.health.totalTextsValue', { count: data.totalKeys })}</span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        {data.overall.map((row) => (
          <div
            key={row.locale}
            style={{
              flex: '1 1 140px',
              minWidth: 140,
              padding: '10px 12px',
              border: '1px solid #e6e6e6',
              borderRadius: 6,
              background: '#fff',
            }}
          >
            <div style={{ fontSize: 12, color: '#888' }}>{languageName(row.locale, t)}</div>
            <div style={{ fontSize: 22, fontWeight: 600 }}>{row.coveragePct}%</div>
            <div style={{ fontSize: 12, color: '#666' }}>
              {row.translated}/{data.totalKeys}
              {row.missing > 0 && (
                <span style={{ color: '#d4380d', marginLeft: 6 }}>· {t('translations.status.missingCount', { count: row.missing })}</span>
              )}
            </div>
            <div
              style={{
                marginTop: 6,
                height: 4,
                background: '#f0f0f0',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${row.coveragePct}%`,
                  height: '100%',
                  background: row.coveragePct >= 95 ? '#52c41a' : row.coveragePct >= 70 ? '#faad14' : '#ff4d4f',
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <details>
        <summary style={{ cursor: 'pointer', fontWeight: 700 }}>
          {t('translations.health.modulesDetail')}
        </summary>
      <div style={{ overflowX: 'auto', border: '1px solid #eee', borderRadius: 4, background: '#fff', marginTop: 12 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#fafafa' }}>
            <tr>
              <th style={hStyle}>{t('translations.columns.module')}</th>
              <th style={{ ...hStyle, textAlign: 'right' }}>{t('translations.columns.texts')}</th>
              {data.locales.map((l) => (
                <th key={l} style={{ ...hStyle, textAlign: 'right' }}>
                  {languageName(l, t)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.namespaces.map((ns) => (
              <tr key={ns.namespace} style={{ borderTop: '1px solid #f0f0f0' }}>
                <td style={cStyle}>
                  {moduleLabel(ns.namespace, t)}
                </td>
                <td style={{ ...cStyle, textAlign: 'right' }}>{ns.totalKeys}</td>
                {data.locales.map((l) => {
                  const stat = ns.perLocale[l] ?? { translated: 0, missing: ns.totalKeys, coveragePct: 0 };
                  const color = stat.coveragePct >= 95 ? '#52c41a' : stat.coveragePct >= 70 ? '#faad14' : '#ff4d4f';
                  return (
                    <td key={l} style={{ ...cStyle, textAlign: 'right', color }}>
                      {stat.coveragePct}%
                      {stat.missing > 0 && (
                        <span style={{ color: '#999', marginLeft: 6, fontSize: 11 }}>
                          <Tag color="warning">{t('translations.status.missingCount', { count: stat.missing })}</Tag>
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </details>
    </Card>
  );
}

function languageName(locale: string, t: (key: string, options?: any) => string) {
  if (locale === 'ar') return t('translations.languages.ar');
  if (locale === 'en') return t('translations.languages.en');
  return locale.toUpperCase();
}

function moduleLabel(namespace: string, t: (key: string, options?: any) => string): string {
  const translated = t(`translations.modules.${String(namespace).toLowerCase()}`, { defaultValue: '' });
  if (translated) return translated;
  return namespace
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

const hStyle: React.CSSProperties = {
  padding: '8px 10px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: '#444',
};
const cStyle: React.CSSProperties = {
  padding: '6px 10px',
};
